import { SortDirection } from "@tm/components"
import { VeFiles } from "@tm/data"
import { useCallback, useEffect, useMemo, useState } from "react"
import { useHistory, useLocation } from "react-router"
import { CategoryBase, FileCategory } from "../models"
import { useLocalization } from "@tm/localization"
import { getCategoryBase } from "../helpers"

const SEARCH_QUERY_PARAM = "query"
const SEARCH_CATEGORY_PARAM = "category"

export function useVefilesSearch() {
    const history = useHistory()
    const location = useLocation()
    const { translateText } = useLocalization()

    const { queryStart, categoryStart } = useMemo(() => {
        const searchParams = new URLSearchParams(location.search)
        return {
            queryStart: searchParams.get(SEARCH_QUERY_PARAM),
            categoryStart: searchParams.get(SEARCH_CATEGORY_PARAM)
        }
    }, [location.search])

    const [tempSearchQuery, setTempSearchQuery] = useState("")
    const [sortOrder, setSortOrder] = useState<VeFiles.FileSortOrder>()
    const [tempSelectedCategory, setTempSelectedCategory] = useState<CategoryBase | undefined>(undefined)

    const selectedCategory = useMemo(() => {
        if (categoryStart && categoryStart in FileCategory && isNaN(Number(categoryStart))) {
            return FileCategory[categoryStart as keyof typeof FileCategory]
        }
        return undefined
    }, [categoryStart])

    useEffect(() => {
        if (!tempSearchQuery && queryStart) {
            setTempSearchQuery(queryStart)
        }

        if (!tempSelectedCategory && categoryStart) {
            if (categoryStart in FileCategory && isNaN(Number(categoryStart))) {
                const category = getCategoryBase(FileCategory[categoryStart as keyof typeof FileCategory], translateText)
                setTempSelectedCategory(category)
            }
        }
    }, [queryStart, categoryStart])

    const handleSearch = useCallback(() => {
        const queryParams = new URLSearchParams(location.search)
        if (!tempSearchQuery) {
            if (queryParams.get(SEARCH_QUERY_PARAM)) {
                queryParams.delete(SEARCH_QUERY_PARAM)
                history.replace({
                    search: queryParams.toString(),
                })
            }

            setTempSearchQuery("")
            return
        }

        queryParams.delete(SEARCH_QUERY_PARAM)
        queryParams.append(SEARCH_QUERY_PARAM, tempSearchQuery)
        
        history.replace({
            search: queryParams.toString(),
        })
    }, [tempSearchQuery, location.search, history])

    const handleSelectedCategory =
        useCallback(
            (category: CategoryBase | undefined) => {
                const queryParams = new URLSearchParams(location.search)
                if (!category || category.categoryValue === FileCategory.None) {
                    if (queryParams.get(SEARCH_CATEGORY_PARAM)) {
                        queryParams.delete(SEARCH_CATEGORY_PARAM)
                        history.replace({
                            search: queryParams.toString(),
                        })
                    }

                    setTempSelectedCategory(undefined)
                    return
                }

                if (category !== tempSelectedCategory) {
                    queryParams.delete(SEARCH_CATEGORY_PARAM)
                    queryParams.append(SEARCH_CATEGORY_PARAM, category.categoryValue ? FileCategory[category.categoryValue] : "")
                    history.replace({
                        search: queryParams.toString(),
                    })
                }
            }
            , [tempSelectedCategory, location.search, history]
        )

    const handleSortOrderClick = useCallback((property: VeFiles.FileSortingField, sortDirection: SortDirection) => {
        setSortOrder({ field: property, descending: sortDirection === "desc" })
    }, [])

    return {
        search: {
            handleSearch,
            tempSearchQuery,
            setTempSearchQuery,
            handleSelectedCategory,
            tempSelectedCategory
        },
        searchQuery: queryStart ?? undefined,
        selectedCategory,
        sortOrder,
        onSort: handleSortOrderClick,
    }
}
